/* eslint-disable react-refresh/only-export-components */
// src/context/ClientContext.tsx
import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";

import { clientService, type Client } from "@/services/clientService";
import {
  getScopedItem,
  setScopedItem,
  removeScopedItem,
} from "@/utils/storage";

import { useAuth } from "./AuthContext";

const MAX_RECENT_CLIENTS = 5;
const RECENT_CLIENTS_KEY = "recent_clients";

interface ClientState {
  clients: Client[];
  currentClient: Client | null;
  recentClients: Client[];
  /** True while initial client list is being fetched */
  isLoading: boolean;
  setCurrentClient: (id: string | null) => void;
  setCurrentClientBySlug: (slugOrId: string | null) => Promise<void>;
  /** Update a single client in the cache (optimistic update) */
  updateClientInCache: (clientId: string, updates: Partial<Client>) => void;
  /** Full reload from server (use sparingly - prefer updateClientInCache) */
  reload: () => Promise<void>;
}

const Ctx = createContext<ClientState | undefined>(undefined);

export function ClientProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [recentClientIds, setRecentClientIds] = useState<string[]>(() => {
    if (typeof window === "undefined") return [];
    return (
      getScopedItem<string[]>(RECENT_CLIENTS_KEY, { userId: user?.id }) || []
    );
  });
  const [currentId, setCurrentId] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return getScopedItem<string>("current_client", { userId: user?.id });
  });

  const currentClient = useMemo(
    () => clients.find((c) => c.id === currentId) ?? null,
    [clients, currentId],
  );

  // Map recent client IDs to actual Client objects (filter out any that no longer exist)
  const recentClients = useMemo(() => {
    return recentClientIds
      .map((id) => clients.find((c) => c.id === id))
      .filter((c): c is Client => c !== undefined);
  }, [recentClientIds, clients]);

  // Track client access (add to recent list)
  const trackClientAccess = useCallback(
    (clientId: string) => {
      setRecentClientIds((prev) => {
        // Remove if already exists, then add to front
        const filtered = prev.filter((id) => id !== clientId);
        const updated = [clientId, ...filtered].slice(0, MAX_RECENT_CLIENTS);

        // Persist to localStorage (user-scoped)
        if (typeof window !== "undefined") {
          setScopedItem(RECENT_CLIENTS_KEY, updated, { userId: user?.id });
        }

        return updated;
      });
    },
    [user?.id],
  );

  async function reload() {
    setIsLoading(true);
    try {
      const list = await clientService.list();
      setClients(list);
    } catch (error) {
      console.error("Error loading clients:", error);
      setClients([]);
    } finally {
      setIsLoading(false);
    }
  }

  const setCurrentClient = useCallback(
    (id: string | null) => {
      setCurrentId(id);
      if (typeof window !== "undefined") {
        const scope = { userId: user?.id };
        if (id) {
          setScopedItem("current_client", id, scope);
          // Track access for recent clients
          trackClientAccess(id);
        } else {
          removeScopedItem("current_client", scope);
        }
      }
    },
    [user?.id, trackClientAccess],
  );

  const setCurrentClientBySlug = useCallback(
    async (slugOrId: string | null) => {
      const scope = { userId: user?.id };

      if (!slugOrId) {
        setCurrentId(null);
        if (typeof window !== "undefined") {
          removeScopedItem("current_client", scope);
        }
        return;
      }

      try {
        const client = await clientService.getBySlugOrId(slugOrId);
        if (client) {
          setCurrentId(client.id);
          if (typeof window !== "undefined") {
            setScopedItem("current_client", client.id, scope);
            // Track access for recent clients
            trackClientAccess(client.id);
          }
        } else {
          console.error(`Client not found for identifier: ${slugOrId}`);
          setCurrentId(null);
          if (typeof window !== "undefined") {
            removeScopedItem("current_client", scope);
          }
        }
      } catch (error) {
        console.error("Error setting current client by slug/ID:", error);
        setCurrentId(null);
        if (typeof window !== "undefined") {
          removeScopedItem("current_client", scope);
        }
      }
    },
    [user?.id, trackClientAccess],
  );

  // Update a single client in the cache without full reload
  const updateClientInCache = useCallback(
    (clientId: string, updates: Partial<Client>) => {
      setClients((prev) =>
        prev.map((client) =>
          client.id === clientId ? { ...client, ...updates } : client,
        ),
      );
    },
    [],
  );

  useEffect(() => {
    void reload().catch(console.error);
  }, []);

  // Re-hydrate user-scoped state once auth finishes bootstrapping.
  //
  // The useState initializers above run synchronously on mount, often before
  // `user` from useAuth() is populated. With `userId: undefined`, getScopedItem
  // falls back to the "anon" scope and returns nothing, so `currentId` and
  // `recentClientIds` end up null/empty even when the user does have a saved
  // selection. This effect re-reads localStorage with the proper scope when
  // user.id first becomes available, without clobbering any selection the user
  // has already made in this session.
  const hydratedForUserRef = useRef<string | null>(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!user?.id) return;
    if (hydratedForUserRef.current === user.id) return;
    hydratedForUserRef.current = user.id;

    const scope = { userId: user.id };
    const storedCurrentId = getScopedItem<string>("current_client", scope);
    if (storedCurrentId) {
      setCurrentId((prev) => prev ?? storedCurrentId);
    }
    const storedRecent =
      getScopedItem<string[]>(RECENT_CLIENTS_KEY, scope) ?? [];
    if (storedRecent.length > 0) {
      setRecentClientIds((prev) => (prev.length > 0 ? prev : storedRecent));
    }
  }, [user?.id]);

  const value = useMemo(
    () => ({
      clients,
      currentClient,
      recentClients,
      isLoading,
      setCurrentClient,
      setCurrentClientBySlug,
      updateClientInCache,
      reload,
    }),
    [
      clients,
      currentClient,
      recentClients,
      isLoading,
      setCurrentClient,
      setCurrentClientBySlug,
      updateClientInCache,
    ],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * Re-provide the client context with an explicit `client` (null = All Clients)
 * for a subtree, leaving `clients` and the setters untouched.
 *
 * `currentClient` is the sticky selection persisted per user, so a portfolio
 * surface such as /cockpit that reads it inherits whichever client was opened
 * last. Wrapping that surface in ClientScope makes every nested `useClient()`
 * see the scope the surface chose instead (Dave, 2026-09-21).
 */
export function ClientScope({
  client,
  children,
}: {
  client: Client | null;
  children: React.ReactNode;
}) {
  const parent = useClient();
  const value = useMemo(
    () => ({ ...parent, currentClient: client }),
    [parent, client],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useClient() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useClient must be used within ClientProvider");
  return ctx;
}
